import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { consultarInmuebles, getPropertyTypes } from '../services/api';
import { FaFilter, FaSort, FaTh, FaList } from 'react-icons/fa';
import PropertyCard from '../components/PropertyCard';

const PropertiesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Estados principales
  const [inmuebles, setInmuebles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' o 'grid'
  
  // Estados para filtros
  const [showFilters, setShowFilters] = useState(false);
  const [showSort, setShowSort] = useState(false);
  const [filtros, setFiltros] = useState({
    cantidadporpagina: 12,
    pagina: 1,
    destinacion: '',
    municipio: '',
    barrio: '',
    clase: '',
    alcobas: 0,
    banos: 0,
    preciodesde: 0,
    preciohasta: 0,
    areadesde: 0,
    areahasta: 0,
    ascensor: false,
    piscina: false,
    unidadcerrada: false,
    parqueadero: false,
    destacado: false,
    inmueble_lujo: false,
    order: 'fecha_consignacion',
    type_order: 'desc'
  });
  
  // Estados para datos auxiliares
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInmuebles, setTotalInmuebles] = useState(0);

  // SEO
  useSEO({
    title: 'Propiedades - J&D Propiedad Raíz',
    description: 'Encuentra la propiedad perfecta en Medellín y el Valle de Aburrá. Apartamentos, casas y locales en venta y arriendo con la mejor asesoría inmobiliaria.',
    keywords: 'propiedades, inmuebles, apartamentos, casas, venta, arriendo, Medellín, Valle de Aburrá',
    ogTitle: 'Propiedades en Venta y Arriendo - J&D Propiedad Raíz',
    ogDescription: 'Descubre las mejores propiedades en Medellín. Apartamentos, casas y locales con precios competitivos y ubicaciones privilegiadas.',
    ogImage: 'https://jdpropiedadraiz.co/logo192.png',
  });

  // Obtener parámetros de búsqueda de la URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const initialFilters = { 
      destinacion: '',
      clase: '',
      municipio: '',
      barrio: '',
      preciodesde: null,
      preciohasta: null,
      pagina: 1,
      cantidadporpagina: 9,
      order: 'precio',
      type_order: 'desc'
    };
    
    // Extraer parámetros de la URL
    if (searchParams.get('destinacion')) initialFilters.destinacion = searchParams.get('destinacion');
    if (searchParams.get('clase')) initialFilters.clase = searchParams.get('clase');
    if (searchParams.get('municipio')) initialFilters.municipio = searchParams.get('municipio');
    if (searchParams.get('barrio')) initialFilters.barrio = searchParams.get('barrio');
    
    setFiltros(initialFilters);
  }, [location.search]);

  // Cargar datos auxiliares
  useEffect(() => {
    const loadAuxiliaryData = async () => {
      try {
        const tipos = await getPropertyTypes();
        setPropertyTypes(tipos);
      } catch (error) {
        console.error('Error cargando datos auxiliares:', error);
      }
    };
    
    loadAuxiliaryData();
  }, []);

  const buscarInmuebles = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const resultados = await consultarInmuebles(filtros);
      setInmuebles(resultados);
      
      if (resultados.length > 0) {
        setTotalPages(resultados[0].totalpaginas || 1);
        setTotalInmuebles(resultados[0].totalinmuebles || 0);
      }
    } catch (error) {
      setError('Error al cargar las propiedades. Por favor, intenta de nuevo.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  // Consultar inmuebles cuando cambien los filtros
  useEffect(() => {
    buscarInmuebles();
  }, [buscarInmuebles]);

  const handleFilterChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor,
      pagina: 1 // Resetear a la primera página
    }));
  };

  const handlePageChange = (nuevaPagina) => {
    setFiltros(prev => ({
      ...prev,
      pagina: nuevaPagina
    }));
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const sortOptions = [
    { value: 'fecha_consignacion-desc', label: 'Más recientes', order: 'fecha_consignacion', type_order: 'desc' },
    { value: 'fecha_ofrecer_inmueble-desc', label: 'Fecha de oferta reciente', order: 'fecha_ofrecer_inmueble', type_order: 'desc' },
    { value: 'precio-asc', label: 'Menor precio', order: 'precio', type_order: 'asc' },
    { value: 'precio-desc', label: 'Mayor precio', order: 'precio', type_order: 'desc' },
    { value: 'consecutivo-desc', label: 'Más nuevos por código', order: 'consecutivo', type_order: 'desc' },
    { value: 'consecutivo-asc', label: 'Más antiguos por código', order: 'consecutivo', type_order: 'asc' },
  ];

  // Generar imagen placeholder como SVG (solo una vez)
  const placeholderImage = useMemo(() => {
    const svg = `
      <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="#0D0D0D"/>
        <rect x="20" y="20" width="360" height="260" fill="none" stroke="#D4AF37" stroke-width="2" stroke-dasharray="10,5"/>
        <g transform="translate(200,120)">
          <rect x="-40" y="-30" width="80" height="60" fill="#D4AF37" opacity="0.3"/>
          <circle cx="-15" cy="-10" r="8" fill="#D4AF37"/>
          <path d="M-5,-5 L15,-5 L10,5 L-5,5 Z" fill="#D4AF37"/>
        </g>
        <text x="200" y="200" text-anchor="middle" fill="#D4AF37" font-family="Arial, sans-serif" font-size="18" font-weight="bold">
          Imagen no disponible
        </text>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  }, []);

  

  return (
    <div className="min-h-screen bg-brand-black text-brand-white">
      {/* Header de la página */}
      <div className="pt-24 pb-8 px-6 border-b border-brand-gold/20">
        <div className="container mx-auto max-w-7xl">
          <h1 className="text-4xl font-bold text-brand-gold mb-4">
            Propiedades
          </h1>
          {totalInmuebles > 0 && (
            <p className="text-brand-gray">
              {totalInmuebles} propiedades encontradas
            </p>
          )}
        </div>
      </div>

      <div className="container mx-auto max-w-7xl px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* Sidebar de filtros - Móvil colapsado */}
          <div className={`lg:w-1/4 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-brand-gold/5 p-6 rounded-lg border border-brand-gold/20 sticky top-24">
              <h2 className="text-xl font-semibold text-brand-gold mb-6">
                Filtros de búsqueda
              </h2>
              
              {/* Filtros aquí - implementar según necesidades */}
              <div className="space-y-4">
                {/* Destinación */}
                <div>
                  <label className="block text-brand-gold font-medium mb-2">
                    Tipo de servicio
                  </label>
                  <select
                    value={filtros.destinacion}
                    onChange={(e) => handleFilterChange('destinacion', e.target.value)}
                    className="w-full px-3 py-2 bg-brand-black border border-brand-gold/30 rounded text-brand-white"
                  >
                    <option value="">Todos</option>
                    <option value="arriendo">Arriendo</option>
                    <option value="venta">Venta</option>
                  </select>
                </div>

                {/* Tipo de propiedad */}
                <div>
                  <label className="block text-brand-gold font-medium mb-2">
                    Tipo de propiedad
                  </label>
                  <select
                    value={filtros.clase}
                    onChange={(e) => handleFilterChange('clase', e.target.value)}
                    className="w-full px-3 py-2 bg-brand-black border border-brand-gold/30 rounded text-brand-white"
                  >
                    <option value="">Todos</option>
                    {propertyTypes.map(tipo => (
                      <option key={tipo.id} value={tipo.nombre}>
                        {tipo.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Alcobas */}
                <div>
                  <label className="block text-brand-gold font-medium mb-2">
                    Habitaciones
                  </label>
                  <select
                    value={filtros.alcobas}
                    onChange={(e) => handleFilterChange('alcobas', parseInt(e.target.value))}
                    className="w-full px-3 py-2 bg-brand-black border border-brand-gold/30 rounded text-brand-white"
                  >
                    <option value={0}>Cualquiera</option>
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                    <option value={3}>3</option>
                    <option value={4}>4</option>
                  </select>
                </div>

                {/* Baños */}
                <div>
                  <label className="block text-brand-gold font-medium mb-2">
                    Baños
                  </label>
                  <select
                    value={filtros.banos}
                    onChange={(e) => handleFilterChange('banos', parseInt(e.target.value))}
                    className="w-full px-3 py-2 bg-brand-black border border-brand-gold/30 rounded text-brand-white"
                  >
                    <option value={0}>Cualquiera</option>
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                    <option value={3}>3</option>
                  </select>
                </div>

                {/* Características especiales */}
                <div>
                  <label className="block text-brand-gold font-medium mb-3">
                    Características
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filtros.ascensor}
                        onChange={(e) => handleFilterChange('ascensor', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm">Ascensor</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filtros.piscina}
                        onChange={(e) => handleFilterChange('piscina', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm">Piscina</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filtros.parqueadero}
                        onChange={(e) => handleFilterChange('parqueadero', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm">Parqueadero</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filtros.unidadcerrada}
                        onChange={(e) => handleFilterChange('unidadcerrada', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm">Unidad cerrada</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contenido principal */}
          <div className="lg:w-3/4">
            {/* Barra de herramientas */}
            <div className="flex flex-wrap items-center justify-between mb-6 gap-4">
              {/* Botones móviles */}
              <div className="flex gap-2 lg:hidden">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center px-4 py-2 bg-brand-gold/20 text-brand-gold rounded-lg border border-brand-gold/40"
                >
                  <FaFilter className="mr-2" size={14} />
                  Filtros
                </button>
              </div>

              {/* Vista y ordenamiento */}
              <div className="flex items-center gap-4">
                {/* Ordenamiento */}
                <div className="relative">
                  <button
                    onClick={() => setShowSort(!showSort)}
                    className="flex items-center px-4 py-2 bg-brand-gold/20 text-brand-gold rounded-lg border border-brand-gold/40"
                  >
                    <FaSort className="mr-2" size={14} />
                    Ordenar
                  </button>
                  
                  {showSort && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-brand-black border border-brand-gold/20 rounded-lg shadow-lg z-10">
                      {sortOptions.map(option => (
                        <button
                          key={option.value}
                          onClick={() => {
                            handleFilterChange('order', option.order);
                            handleFilterChange('type_order', option.type_order);
                            setShowSort(false);
                          }}
                          className="w-full text-left px-4 py-2 text-brand-white hover:bg-brand-gold/20 first:rounded-t-lg last:rounded-b-lg"
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Cambio de vista */}
                <div className="flex border border-brand-gold/40 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 ${viewMode === 'list' ? 'bg-brand-gold text-brand-black' : 'bg-transparent text-brand-gold'}`}
                  >
                    <FaList size={16} />
                  </button>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 ${viewMode === 'grid' ? 'bg-brand-gold text-brand-black' : 'bg-transparent text-brand-gold'}`}
                  >
                    <FaTh size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Resultados */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold mx-auto mb-4"></div>
                <p className="text-brand-gray">Cargando propiedades...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-400 mb-4">{error}</p>
                <button
                  onClick={buscarInmuebles}
                  className="px-6 py-2 bg-brand-gold text-brand-black rounded-lg hover:bg-brand-gold/90"
                >
                  Reintentar
                </button>
              </div>
            ) : inmuebles.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-brand-gray mb-4">No se encontraron propiedades con los filtros seleccionados.</p>
                <button
                  onClick={() => setFiltros({ ...filtros, pagina: 1 })}
                  className="px-6 py-2 bg-brand-gold text-brand-black rounded-lg hover:bg-brand-gold/90"
                >
                  Limpiar filtros
                </button>
              </div>
            ) : (
              <>
                {/* Lista de propiedades */}
                <div className={`${viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6' : 'space-y-6'}`}>
                  {inmuebles.map(inmueble => (
                    <PropertyCard
                      key={inmueble.consecutivo}
                      inmueble={inmueble}
                      isGrid={viewMode === 'grid'}
                    />
                  ))}
                </div>

                {/* Paginación */}
                {totalPages > 1 && (
                  <div className="flex justify-center mt-12">
                    <div className="flex gap-2">
                      {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-4 py-2 rounded-lg ${
                              filtros.pagina === page
                                ? 'bg-brand-gold text-brand-black'
                                : 'bg-brand-gold/20 text-brand-gold hover:bg-brand-gold/30'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertiesPage;
